/**
 * Exercise Prescription — wellness tenants.
 *
 * - Browse routines for the current patient (progress, status pills)
 * - Build new routine with picker from EXERCISE_LIBRARY (category-grouped)
 * - Per-exercise overrides: sets, reps, hold, note
 * - Toggle active / reminder, log admin completions, delete
 * - Generate patient-portal link (public token) with copy + WhatsApp share
 * - Future-ready: ai_summary / ai_progress_analysis / ai_adherence_score fields
 *   already persisted on the routine, no UI implementation yet
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Activity, Plus, Loader2, Trash2, Check, CheckCircle2, Dumbbell,
  Sparkles, Copy, MessageSquare, Link as LinkIcon, Calendar, Repeat,
  ChevronDown, ChevronRight, X, Clock, Target, Layers,
} from "lucide-react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const FREQ_LABELS = {
  daily:        "Quotidiana",
  every_2_days: "A giorni alterni",
  weekly:       "Settimanale",
  custom:       "Personalizzata",
};

const ExercisesTab = ({ clientId }) => {
  const [routines, setRoutines] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [library,  setLibrary]  = useState({ exercises: [], categories: {} });
  const [openNew,  setOpenNew]  = useState(false);
  const [share,    setShare]    = useState(null); // routine with public_token

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, lib] = await Promise.all([
        axios.get(`${API}/wellness/exercises/routines/${clientId}`, { withCredentials: true }),
        axios.get(`${API}/wellness/exercises/library`, { withCredentials: true }),
      ]);
      setRoutines(r.data.routines || []);
      setLibrary({
        exercises: lib.data.exercises || [],
        categories: lib.data.categories || {},
      });
    } catch {
      toast.error("Errore caricamento esercizi");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="exercises-tab">
      {/* HERO */}
      <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-amber-500/5 via-transparent to-emerald-500/5 dark:from-amber-500/10 dark:to-emerald-500/10 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-amber-500 to-emerald-500 flex items-center justify-center shadow">
              <Dumbbell className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-600 dark:text-amber-400">Exercise Prescription</p>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">Routine personalizzate</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {routines.length} {routines.length === 1 ? "routine" : "routine"} ·
                {" "}{library.exercises.length} esercizi nel catalogo
              </p>
            </div>
          </div>
          <Button
            onClick={() => setOpenNew(true)}
            className="rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 hover:opacity-90 text-white"
            data-testid="routine-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nuova routine
          </Button>
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <div className="space-y-2">
          {[0,1].map(i => <div key={i} className="h-32 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />)}
        </div>
      ) : routines.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center" data-testid="routines-empty">
          <div className="mx-auto w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-100 to-emerald-100 dark:from-amber-900/30 dark:to-emerald-900/30 flex items-center justify-center mb-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
          </div>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Nessuna routine ancora</p>
          <p className="text-xs text-slate-400 mt-1">Crea la prima routine per assegnare esercizi a domicilio al paziente.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="routines-list">
          {routines.map(r => (
            <RoutineCard
              key={r.routine_id}
              routine={r}
              onChanged={load}
              onShare={() => setShare(r)}
            />
          ))}
        </ul>
      )}

      <NewRoutineDialog
        open={openNew}
        onOpenChange={setOpenNew}
        clientId={clientId}
        library={library}
        onCreated={(routine) => { setOpenNew(false); load(); setShare(routine); }}
      />
      <ShareRoutineDialog routine={share} onClose={() => setShare(null)} />
    </div>
  );
};

/* ============================================================ */
/* ROUTINE CARD                                                 */
/* ============================================================ */
const RoutineCard = ({ routine, onChanged, onShare }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const r = routine;

  const isExpired = r.ends_at && new Date(r.ends_at) < new Date();
  const status = !r.active ? "paused" : isExpired ? "expired" : "active";
  const statusMeta = {
    active:  { label: "Attiva",   cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
    paused:  { label: "Sospesa",  cls: "bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300" },
    expired: { label: "Scaduta",  cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  }[status];

  const toggleActive = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/wellness/exercises/routines/${r.routine_id}`,
        { active: !r.active }, { withCredentials: true });
      toast.success(r.active ? "Routine sospesa" : "Routine riattivata");
      onChanged();
    } catch {
      toast.error("Errore aggiornamento");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare la routine "${r.name}"?`)) return;
    try {
      await axios.delete(`${API}/wellness/exercises/routines/${r.routine_id}`, { withCredentials: true });
      toast.success("Routine eliminata");
      onChanged();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const logComplete = async (exerciseCode) => {
    try {
      await axios.post(`${API}/wellness/exercises/routines/${r.routine_id}/complete`,
        { exercise_code: exerciseCode }, { withCredentials: true });
      toast.success("Esercizio segnato come fatto");
      onChanged();
    } catch {
      toast.error("Errore");
    }
  };

  const doneTodayCodes = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return new Set((r.completions || [])
      .filter(c => (c.date || "").slice(0, 10) === today)
      .map(c => c.exercise_code));
  }, [r.completions]);

  return (
    <li
      className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden hover:border-amber-300 transition-all"
      data-testid={`routine-card-${r.routine_id}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-slate-900 dark:text-white truncate">{r.name}</h4>
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${statusMeta.cls}`}>
                {statusMeta.label}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              <Repeat className="inline w-3 h-3 mr-0.5" />
              {FREQ_LABELS[r.frequency] || r.frequency} ·
              {" "}<Layers className="inline w-3 h-3 mr-0.5" />{r.total_exercises} esercizi
              {r.ends_at && <> · <Calendar className="inline w-3 h-3 mr-0.5" />Fine {fmtDate(r.ends_at)}</>}
            </p>
            {r.description && <p className="text-[11px] text-slate-500 mt-1 italic line-clamp-2">{r.description}</p>}
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Oggi</p>
            <p className="font-black text-xl text-slate-900 dark:text-white font-variant-numeric tabular-nums">
              {r.completed_today}<span className="text-slate-400 text-sm font-normal">/{r.total_exercises}</span>
            </p>
            <p className="text-[10px] text-slate-400">{r.progress_today_pct}%</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-3">
          <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-500"
              style={{ width: `${r.progress_today_pct}%` }}
              data-testid={`routine-progress-${r.routine_id}`}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button
            size="sm" variant="outline"
            onClick={() => setExpanded(v => !v)}
            className="h-7 rounded-full text-[11px]"
            data-testid={`routine-expand-${r.routine_id}`}
          >
            {expanded ? <ChevronDown className="w-3 h-3 mr-1" /> : <ChevronRight className="w-3 h-3 mr-1" />}
            Esercizi
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={onShare}
            className="h-7 rounded-full text-[11px]"
            data-testid={`routine-share-${r.routine_id}`}
          >
            <LinkIcon className="w-3 h-3 mr-1" /> Link paziente
          </Button>
          <div className="flex items-center gap-1.5 ml-auto">
            <Switch
              checked={!!r.active}
              onCheckedChange={toggleActive}
              disabled={busy}
              data-testid={`routine-active-${r.routine_id}`}
            />
            <span className="text-[10px] text-slate-400">Attiva</span>
            <Button
              size="sm" variant="ghost"
              onClick={remove}
              className="rounded-full text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 h-7 w-7 p-0"
              data-testid={`routine-delete-${r.routine_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 dark:border-white/10 bg-slate-50/60 dark:bg-white/[0.02] p-3 space-y-1.5" data-testid={`routine-exercises-${r.routine_id}`}>
          {r.exercises.map(ex => {
            const done = doneTodayCodes.has(ex.code);
            return (
              <div key={ex.code} className="flex items-center gap-2 rounded-xl bg-white dark:bg-slate-900/40 border border-slate-200/60 dark:border-white/10 p-2.5">
                <button
                  onClick={() => !done && logComplete(ex.code)}
                  disabled={done}
                  className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                    done
                      ? "bg-emerald-500 text-white"
                      : "border-2 border-slate-300 dark:border-white/20 hover:border-emerald-500"
                  }`}
                  data-testid={`exercise-complete-${ex.code}`}
                >
                  {done && <Check className="w-3.5 h-3.5" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{ex.name}</p>
                  <p className="text-[10px] text-slate-500">
                    <Target className="inline w-2.5 h-2.5 mr-0.5" />
                    {ex.sets}×{ex.reps}
                    {ex.hold_sec > 0 && <> · <Clock className="inline w-2.5 h-2.5 mr-0.5" />{ex.hold_sec}s hold</>}
                  </p>
                  {ex.note && <p className="text-[10px] italic text-slate-400 mt-0.5">{ex.note}</p>}
                </div>
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{ex.category}</span>
              </div>
            );
          })}
        </div>
      )}
    </li>
  );
};

/* ============================================================ */
/* NEW ROUTINE DIALOG                                           */
/* ============================================================ */
const NewRoutineDialog = ({ open, onOpenChange, clientId, library, onCreated }) => {
  const [name, setName]   = useState("");
  const [desc, setDesc]   = useState("");
  const [freq, setFreq]   = useState("daily");
  const [days, setDays]   = useState(14);
  const [picked, setPicked] = useState({}); // code -> { sets, reps, hold_sec, note }
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState("all");

  useEffect(() => {
    if (open) {
      setName("Routine domiciliare"); setDesc(""); setFreq("daily"); setDays(14);
      setPicked({}); setFilterCat("all");
    }
  }, [open]);

  const toggle = (ex) => {
    setPicked(p => {
      const c = { ...p };
      if (c[ex.code]) {
        delete c[ex.code];
      } else {
        c[ex.code] = {
          sets:     ex.default_sets,
          reps:     ex.default_reps,
          hold_sec: ex.default_hold_sec,
          note:     "",
        };
      }
      return c;
    });
  };

  const updateField = (code, field, value) => {
    setPicked(p => ({ ...p, [code]: { ...p[code], [field]: value } }));
  };

  const categoryGroups = useMemo(() => {
    const groups = {};
    library.exercises.forEach(ex => {
      if (filterCat !== "all" && ex.category !== filterCat) return;
      groups[ex.category] = groups[ex.category] || [];
      groups[ex.category].push(ex);
    });
    return groups;
  }, [library.exercises, filterCat]);

  const submit = async () => {
    const codes = Object.keys(picked);
    if (codes.length === 0) { toast.error("Seleziona almeno un esercizio"); return; }
    if (!name.trim()) { toast.error("Inserisci un nome"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/wellness/exercises/routines/${clientId}`, {
        name:          name.trim(),
        description:   desc.trim() || null,
        frequency:     freq,
        duration_days: Number(days) || 14,
        exercises:     codes.map(code => ({
          code,
          sets:     Number(picked[code].sets)     || undefined,
          reps:     Number(picked[code].reps)     || undefined,
          hold_sec: Number(picked[code].hold_sec) || 0,
          note:     picked[code].note?.trim() || null,
        })),
      }, { withCredentials: true });
      toast.success("Routine creata");
      onCreated(r.data.routine);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="routine-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-amber-500 to-emerald-500 flex items-center justify-center">
              <Dumbbell className="w-3.5 h-3.5 text-white" />
            </div>
            Nuova routine esercizi
          </DialogTitle>
          <DialogDescription>
            Componi la routine selezionando dagli esercizi del catalogo. Puoi personalizzare serie, ripetizioni e tempo di tenuta.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nome routine</label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="routine-new-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Frequenza</label>
            <Select value={freq} onValueChange={setFreq}>
              <SelectTrigger data-testid="routine-new-freq"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Quotidiana</SelectItem>
                <SelectItem value="every_2_days">A giorni alterni</SelectItem>
                <SelectItem value="weekly">Settimanale</SelectItem>
                <SelectItem value="custom">Personalizzata</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Durata (giorni)</label>
            <Input type="number" min="1" max="365" value={days} onChange={e => setDays(e.target.value)} data-testid="routine-new-days" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Descrizione</label>
            <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Opzionale" data-testid="routine-new-desc" />
          </div>
        </div>

        {/* Library picker */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Catalogo esercizi · {Object.keys(picked).length} selezionati
            </p>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="h-7 w-44 text-xs" data-testid="routine-new-filter-cat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {Object.entries(library.categories).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
            {Object.entries(categoryGroups).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1">
                  {library.categories[cat] || cat}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {items.map(ex => {
                    const on = !!picked[ex.code];
                    return (
                      <button
                        key={ex.code}
                        onClick={() => toggle(ex)}
                        className={`flex items-start gap-2 p-2 rounded-xl border-2 text-left transition-all ${
                          on
                            ? "border-amber-500 bg-amber-50/60 dark:bg-amber-900/20"
                            : "border-slate-200 dark:border-white/10 hover:border-slate-300"
                        }`}
                        data-testid={`exercise-option-${ex.code}`}
                      >
                        <div className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center ${
                          on ? "bg-amber-500 text-white" : "border-2 border-slate-300 dark:border-white/20"
                        }`}>
                          {on && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{ex.name}</p>
                          <p className="text-[10px] text-slate-500">
                            {ex.default_sets}×{ex.default_reps}
                            {ex.default_hold_sec > 0 && <> · {ex.default_hold_sec}s</>}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Customization for picked items */}
        {Object.keys(picked).length > 0 && (
          <div className="mt-3 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/10 p-3 space-y-2 max-h-[30vh] overflow-y-auto">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Personalizza esercizi selezionati</p>
            {Object.entries(picked).map(([code, vals]) => {
              const lib = library.exercises.find(e => e.code === code);
              if (!lib) return null;
              return (
                <div key={code} className="bg-white dark:bg-slate-900/40 rounded-xl border border-slate-200/60 dark:border-white/10 p-2">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{lib.name}</p>
                    <button
                      onClick={() => toggle(lib)}
                      className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-full p-1"
                      data-testid={`exercise-remove-${code}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    <NumField label="Set" v={vals.sets} on={v => updateField(code, "sets", v)} t={`set-${code}`} />
                    <NumField label="Rip" v={vals.reps} on={v => updateField(code, "reps", v)} t={`reps-${code}`} />
                    <NumField label="Hold s" v={vals.hold_sec} on={v => updateField(code, "hold_sec", v)} t={`hold-${code}`} />
                    <Input
                      value={vals.note}
                      onChange={e => updateField(code, "note", e.target.value)}
                      placeholder="Note"
                      className="text-xs h-8"
                      data-testid={`note-${code}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button
            onClick={submit}
            disabled={saving || Object.keys(picked).length === 0}
            className="bg-gradient-to-r from-amber-500 to-emerald-500 text-white"
            data-testid="routine-new-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Crea routine
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NumField = ({ label, v, on, t }) => (
  <div>
    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block">{label}</label>
    <Input
      type="number" min="0" value={v ?? 0}
      onChange={e => on(e.target.value)}
      className="text-xs h-8 font-mono"
      data-testid={t}
    />
  </div>
);

/* ============================================================ */
/* SHARE ROUTINE DIALOG                                         */
/* ============================================================ */
const ShareRoutineDialog = ({ routine, onClose }) => {
  const open = !!routine;
  if (!routine) return null;
  const baseUrl = window.location.origin;
  const path = `/exercises/${routine.public_token}`;
  const fullUrl = `${baseUrl}${path}`;
  const waText = `Ciao! Ti ho preparato la tua routine personalizzata "${routine.name}". Apri il link per vedere gli esercizi: ${fullUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copiato");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="routine-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-white" />
            </div>
            Link per il paziente
          </DialogTitle>
          <DialogDescription>
            Condividi questo link con il paziente: potrà vedere gli esercizi e segnarli come completati.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-2.5 text-xs font-mono break-all text-slate-700 dark:text-slate-200">
          {fullUrl}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={copy}
            variant="outline"
            className="rounded-2xl py-5"
            data-testid="routine-share-copy"
          >
            <Copy className="w-4 h-4 mr-2" /> Copia link
          </Button>
          <Button
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, "_blank")}
            className="rounded-2xl py-5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
            data-testid="routine-share-whatsapp"
          >
            <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExercisesTab;
